// src/hooks/useNewItemsLogic.jsx - Hook Actualizado para manejar productos sin stock
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSalesContext } from "../contexts/SalesContext";
import { useSupaContext } from "../contexts/SupaContext";
import { useInventoryPublic } from "./useInventoryPublic";
import toast from "react-hot-toast";

// ================================
// CONFIGURACIÓN DE PASOS
// ================================
const STEP_CONFIG = {
  BOTTLES: "bottles",
  DISPENSERS: "dispensers",
  DELIVERY: "delivery",
  SCHEDULE: "schedule",
  SUMMARY: "summary",
};

// ================================
// HOOK PRINCIPAL ACTUALIZADO
// ================================
export const useNewItemsLogic = ({
  showBottles = true,
  showDispensers = true,
}) => {
  const navigate = useNavigate();

  // ================================
  // CONTEXTOS BASE
  // ================================
  const {
    selectedSugs,
    selectedDispensers,
    selectedAddress,
    selectedCoordinates,
    selectedDeliveryDate,
    selectedTimeSlot,
    newOrderTotal,
    newSalesTerms,
    isCoordsLoading,
    isValid: addressIsValid,
    progress,
    triggerNewShopLeadGeneration,
    
  } = useSalesContext();

  const { user, isActiveUser } = useSupaContext();

  const {
    jugs: jugStock,
    dispensers: dispenserStock,
    isLoaded: isStockLoaded,
    isRefreshing: isUpdatingStock,
    hasStock,
    shouldShowDispensers: inventoryHasDispensers,
    refreshStock,
    error: stockError,
    cacheInfo,
  } = useInventoryPublic();

  // ================================
  // ESTADOS LOCALES
  // ================================
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ================================
  // LÓGICA DE PRODUCTOS MEJORADA - SEPARAR HABILITADOS DE CON STOCK
  // ================================

  // Productos configurados para mostrar (independiente del stock/inventario)
  const configuredProducts = useMemo(() => {
    const products = [];

    if (showBottles) {
      products.push({
        type: "bottles",
        name: "botellones",
        enabled: true,
        displayName: "Botellones 20L",
      });
    }

    if (showDispensers) {
      products.push({
        type: "dispensers",
        name: "dispensadores",
        enabled: true,
        displayName: "Dispensadores",
      });
    }

    return products;
  }, [showBottles, showDispensers]);

  // Productos habilitados con información de stock
  const enabledProducts = useMemo(() => {
    return configuredProducts.map((product) => ({
      ...product,
      selected:
        product.type === "bottles"
          ? selectedSugs || 0
          : selectedDispensers || 0,
      available:
        product.type === "bottles" ? jugStock || 0 : dispenserStock || 0,
      hasStock:
        product.type === "bottles"
          ? jugStock > 0
          : dispenserStock > 0 && inventoryHasDispensers,
    }));
  }, [
    configuredProducts,
    selectedSugs,
    selectedDispensers,
    jugStock,
    dispenserStock,
    inventoryHasDispensers,
  ]);

  // Productos con stock disponible
  const productsWithStock = useMemo(() => {
    return enabledProducts.filter((product) => product.hasStock);
  }, [enabledProducts]);

  // Productos seleccionados con stock válido
  const selectedProductsWithStock = useMemo(() => {
    return enabledProducts.filter(
      (product) => product.selected > 0 && product.hasStock
    );
  }, [enabledProducts]);

  // ================================
  // VALIDACIONES ACTUALIZADAS
  // ================================

  // ¿Hay productos habilitados? (independiente del stock)
  const hasEnabledProducts = useMemo(() => {
    return enabledProducts.length > 0;
  }, [enabledProducts]);

  // ¿Hay productos con stock disponible?
  const hasProductsWithStock = useMemo(() => {
    return productsWithStock.length > 0;
  }, [productsWithStock]);

  // ¿Hay al menos un producto seleccionado? (sin importar stock)
  const hasSelectedProducts = useMemo(() => {
    const result = selectedSugs > 0 || selectedDispensers > 0;
    console.log(
      `🔄 hasSelectedProducts: ${result} (sugs: ${selectedSugs}, dispensers: ${selectedDispensers})`
    );
    return result;
  }, [selectedSugs, selectedDispensers]);

  // ¿Hay productos seleccionados Y con stock válido?
  const hasValidSelectedProducts = useMemo(() => {
    const result =
      selectedProductsWithStock.length > 0 &&
      selectedProductsWithStock.some((p) => p.selected > 0);
    console.log(
      `🔄 hasValidSelectedProducts: ${result} (productos con stock seleccionados: ${selectedProductsWithStock.length})`
    );
    return result;
  }, [selectedProductsWithStock]);

  // ================================
  // LÓGICA DE PASOS ACTUALIZADA
  // ================================
  const availableSteps = useMemo(() => {
    const steps = [];

    // Mostrar pasos de productos si están habilitados (independiente del stock)
    if (showBottles) {
      steps.push(STEP_CONFIG.BOTTLES);
    }

    if (showDispensers) {
      steps.push(STEP_CONFIG.DISPENSERS);
    }

    steps.push(STEP_CONFIG.DELIVERY, STEP_CONFIG.SCHEDULE, STEP_CONFIG.SUMMARY);

    return steps;
  }, [showBottles, showDispensers]);

  const currentStep = availableSteps[currentStepIndex] || STEP_CONFIG.BOTTLES;

  // ¿Es un paso de producto?
  const isProductStep = useMemo(() => {
    return [STEP_CONFIG.BOTTLES, STEP_CONFIG.DISPENSERS].includes(currentStep);
  }, [currentStep]);

  // ¿Es el último paso de producto disponible?
  const isLastProductStep = useMemo(() => {
    if (!isProductStep) return false;

    const productSteps = availableSteps.filter((step) =>
      [STEP_CONFIG.BOTTLES, STEP_CONFIG.DISPENSERS].includes(step)
    );

    if (productSteps.length === 0) return false;

    const lastProductStep = productSteps[productSteps.length - 1];
    const result = currentStep === lastProductStep;
    console.log(
      `🔄 isLastProductStep: ${result} (currentStep: ${currentStep}, lastProductStep: ${lastProductStep})`
    );
    return result;
  }, [isProductStep, availableSteps, currentStep]);

  // ================================
  // VALIDACIONES CRÍTICAS ACTUALIZADAS
  // ================================

  // Función para determinar si puede continuar (actualizada)
  const canContinueFromCurrentStep = useMemo(() => {
    console.log(`🔄 Evaluando canContinue para paso: ${currentStep}`);

    switch (currentStep) {
      case STEP_CONFIG.BOTTLES:
      case STEP_CONFIG.DISPENSERS:
        // En pasos de productos: siempre puede navegar ENTRE productos
        // Solo bloquear si es el último paso de productos Y no hay productos válidos seleccionados
        if (isLastProductStep) {
          const canContinue = hasValidSelectedProducts;
          console.log(
            `${
              canContinue ? "✅" : "❌"
            } Último paso de productos - productos válidos: ${hasValidSelectedProducts}`
          );
          return canContinue; // CAMBIO IMPORTANTE: Ahora retorna basado en productos válidos
        }
        console.log(`✅ Paso de producto - puede continuar`);
        return true;

      case STEP_CONFIG.DELIVERY:
        const deliveryCanContinue =
          hasValidSelectedProducts &&
          isActiveUser &&
          selectedAddress &&
          !isCoordsLoading;
        console.log(
          `${
            deliveryCanContinue ? "✅" : "❌"
          } Delivery - productos válidos: ${hasValidSelectedProducts}, auth: ${isActiveUser}, dirección: ${!!selectedAddress}, loading: ${isCoordsLoading}`
        );
        return deliveryCanContinue;

      case STEP_CONFIG.SCHEDULE:
        const scheduleCanContinue =
          hasValidSelectedProducts &&
          isActiveUser &&
          selectedDeliveryDate &&
          selectedTimeSlot;
        console.log(
          `${
            scheduleCanContinue ? "✅" : "❌"
          } Schedule - productos válidos: ${hasValidSelectedProducts}, auth: ${isActiveUser}, fecha: ${!!selectedDeliveryDate}, horario: ${!!selectedTimeSlot}`
        );
        return scheduleCanContinue;

      case STEP_CONFIG.SUMMARY:
        const summaryCanContinue =
          hasValidSelectedProducts && isActiveUser && newSalesTerms;
        console.log(
          `${
            summaryCanContinue ? "✅" : "❌"
          } Summary - productos válidos: ${hasValidSelectedProducts}, auth: ${isActiveUser}, términos: ${newSalesTerms}`
        );
        return summaryCanContinue;

      default:
        console.log(`❌ Paso desconocido: ${currentStep}`);
        return false;
    }
  }, [
    currentStep,
    isLastProductStep,
    hasValidSelectedProducts,
    isActiveUser,
    isCoordsLoading,
    selectedAddress,
    selectedDeliveryDate,
    selectedTimeSlot,
    newSalesTerms,
  ]);

  // Función para obtener mensaje de error específico (actualizada)
  const getValidationMessage = useMemo(() => {
    if (canContinueFromCurrentStep) return null;

    switch (currentStep) {
      case STEP_CONFIG.BOTTLES:
      case STEP_CONFIG.DISPENSERS:
        if (isLastProductStep && !hasValidSelectedProducts) {
          if (!hasSelectedProducts) {
            return "Agrega al menos un producto para continuar";
          } else if (!hasProductsWithStock) {
            return "Los productos seleccionados no tienen stock disponible";
          } else {
            return "Selecciona productos que tengan stock disponible";
          }
        }
        return null;

      case STEP_CONFIG.DELIVERY:
        if (!hasValidSelectedProducts) {
          if (!hasSelectedProducts) {
            return "Agrega al menos un producto para continuar";
          } else {
            return "Los productos seleccionados no tienen stock disponible";
          }
        }
        if (!isActiveUser) return "Inicia sesión para continuar";
        if (!selectedAddress) return "Selecciona una dirección de entrega";
        if (isCoordsLoading) return "Validando dirección...";
        if (addressIsValid === false) return "Dirección fuera de cobertura";
        return null;

      case STEP_CONFIG.SCHEDULE:
        if (!hasValidSelectedProducts) {
          return "Los productos seleccionados no tienen stock disponible";
        }
        if (!isActiveUser) return "Inicia sesión para continuar";
        if (!selectedDeliveryDate) return "Selecciona una fecha de entrega";
        if (!selectedTimeSlot) return "Selecciona un horario de entrega";
        return null;

      case STEP_CONFIG.SUMMARY:
        if (!hasValidSelectedProducts) {
          return "Los productos seleccionados no tienen stock disponible";
        }
        if (!isActiveUser) return "Inicia sesión para continuar";
        if (!newSalesTerms) return "Acepta los términos y condiciones";
        return null;

      default:
        return "Error de validación";
    }
  }, [
    canContinueFromCurrentStep,
    currentStep,
    isLastProductStep,
    hasValidSelectedProducts,
    hasSelectedProducts,
    hasProductsWithStock,
    isActiveUser,
    isCoordsLoading,
    selectedAddress,
    addressIsValid,
    selectedDeliveryDate,
    selectedTimeSlot,
    newSalesTerms,
  ]);

  // ================================
  // ESTADO FINAL DEL BOTÓN
  // ================================

  const isButtonDisabled = useMemo(() => {
    const disabled =
      isUpdatingStock || isSubmitting || !canContinueFromCurrentStep;
    console.log(
      `🔄 isButtonDisabled: ${disabled} (updating: ${isUpdatingStock}, submitting: ${isSubmitting}, canContinue: ${canContinueFromCurrentStep})`
    );
    return disabled;
  }, [isUpdatingStock, isSubmitting, canContinueFromCurrentStep]);

  const getButtonText = useMemo(() => {
    if (isSubmitting) return "Procesando...";

    if (currentStep === STEP_CONFIG.SUMMARY) return "Confirmar e ir a pagar";

    return "Continuar";
  }, [isSubmitting, currentStep]);

  // ================================
  // HANDLERS ACTUALIZADOS
  // ================================

  const handleSubmitOrder = useCallback(async () => {
    if (isSubmitting) {
      console.log("⚠️ handleSubmitOrder: Ya se está procesando");
      return;
    }
    setIsSubmitting(true);

    try {
      // Solo disparar el trigger UNA VEZ
      await triggerNewShopLeadGeneration();
      console.log("✅ useNewItemsLogic: Orden iniciada correctamente");
    } catch (error) {
      console.error("❌ Error al iniciar orden:", error);
      // IMPORTANTE: Resetear el estado si hay error
      setIsSubmitting(false);
      toast.error("Error al procesar tu pedido. Intenta de nuevo.");
    }
    // NO resetear isSubmitting en success, dejar que el componente se desmonte
  }, [triggerNewShopLeadGeneration, isSubmitting]);

  const handleNext = useCallback(() => {
    console.log(
      "🔄 handleNext: Paso actual",
      availableSteps[currentStepIndex],
      `(${currentStepIndex})`
    );

    if (!canContinueFromCurrentStep) {
      const message = getValidationMessage;
      if (message) {
        toast.error(message, {
          duration: 3000,
          position: "top-center",
        });
      }
      return;
    }

    if (isSubmitting) {
      console.log("⚠️ handleNext: Ya se está procesando una orden");
      return;
    }

    // Manejar envío final
    if (currentStep === STEP_CONFIG.SUMMARY) {
      console.log(`📤 Enviando pedido final`);
      handleSubmitOrder();
      return;
    }

    // Verificar el siguiente paso
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < availableSteps.length) {
      const nextStep = availableSteps[nextStepIndex];

      // Solo verificar si el siguiente paso es DELIVERY (primera vez que necesita productos válidos)
      if (nextStep === STEP_CONFIG.DELIVERY && !hasValidSelectedProducts) {
        console.log(`🛒 Siguiente paso requiere productos con stock válido`);
        toast.error(
          "Agrega al menos un producto con stock disponible antes de continuar",
          {
            duration: 4000,
            position: "top-center",
            style: {
              marginTop: "60px",
              background: "#FEF2F2",
              color: "#991B1B",
              border: "1px solid #FECACA",
            },
          }
        );
        return;
      }

      // Verificar autenticación para pasos que la requieren
      if (
        [
          STEP_CONFIG.DELIVERY,
          STEP_CONFIG.SCHEDULE,
          STEP_CONFIG.SUMMARY,
        ].includes(nextStep) &&
        !isActiveUser
      ) {
        console.log(`🔐 Siguiente paso requiere auth`);
        setAccessModalOpen(true);
        return;
      }
    }

    // Avanzar
    if (currentStepIndex < availableSteps.length - 1) {
      console.log(`➡️ Avanzando a paso ${currentStepIndex + 2}`);
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [
    currentStep,
    currentStepIndex,
    availableSteps,
    canContinueFromCurrentStep,
    getValidationMessage,
    hasValidSelectedProducts,
    isActiveUser,
    handleSubmitOrder,
  ]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      console.log(`⬅️ Retrocediendo a paso ${currentStepIndex}`);
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);

  // ================================
  // EFECTOS
  // ================================

  // Redirección cuando se procesa el pedido
  useEffect(() => {
    if (progress === 100 || progress === 1 || progress === 2) {
      navigate("/pedido-realizado");
    }
  }, [progress, navigate]);

  // Cerrar modal de autenticación y avanzar si corresponde
  useEffect(() => {
    if (isActiveUser && accessModalOpen) {
      setAccessModalOpen(false);

      setTimeout(() => {
        if (
          canContinueFromCurrentStep &&
          currentStepIndex < availableSteps.length - 1
        ) {
          setCurrentStepIndex(currentStepIndex + 1);
        }
      }, 300);
    }
  }, [
    isActiveUser,
    accessModalOpen,
    canContinueFromCurrentStep,
    currentStepIndex,
    availableSteps.length,
  ]);

  // Detectar móvil
  useEffect(() => {
    const checkBreakpoint = () => setIsMobile(window.innerWidth < 768);
    checkBreakpoint();
    window.addEventListener("resize", checkBreakpoint);
    return () => window.removeEventListener("resize", checkBreakpoint);
  }, []);

  // ================================
  // RETORNO ACTUALIZADO
  // ================================
  return {
    // Estados principales
    currentStep,
    currentStepIndex,
    setCurrentStepIndex,
    availableSteps,

    // Validaciones actualizadas
    enabledProducts,
    hasEnabledProducts,
    hasProductsWithStock,
    hasSelectedProducts,
    hasValidSelectedProducts,
    selectedProductsWithStock,
    productsWithStock,
    isProductStep,
    isLastProductStep,

    // Stock (mantener compatibilidad)
    stockLevels: { jugs: jugStock, dispensers: dispenserStock },
    isStockLoaded,
    isUpdatingStock,
    hasStock,
    stockError,
    refreshStock,

    // Botón y validación final
    isButtonDisabled,
    getButtonText,
    canContinueFromCurrentStep,
    getCurrentValidationMessage: () => getValidationMessage,

    // Navegación
    handleNext,
    handleBack,
    handleSubmitOrder,

    // UI
    accessModalOpen,
    setAccessModalOpen,
    isSubmitting,
    isMobile,

    // Estados del contexto para TotalBar
    newOrderTotal,
    isCoordsLoading,
    progress,
    newSalesTerms,

    // Constantes
    STEP_CONFIG,

    // Cache info
    stockCacheStatus: {
      isUsingCache: cacheInfo.hasCachedData && !cacheInfo.isExpired,
      cacheAge: cacheInfo.cacheAge,
      shouldRefresh: cacheInfo.isExpired,
      lastUpdate: cacheInfo.lastCacheUpdate,
    },
    goToStep: (stepName) => {
      const index = availableSteps.indexOf(stepName);
      if (index !== -1) {
        setCurrentStepIndex(index);
      }
    },
  };
};
